import React, { createContext, useContext, useState, useRef, useCallback } from "react";
import AppConfirmDialog from "./AppConfirmDialog";

const ConfirmContext = createContext(null);

/**
 * ConfirmProvider - Cho phép gọi dialog xác nhận bằng hàm useConfirm()
 * mà không cần phải khai báo state mở/đóng ở từng component.
 */
export function ConfirmProvider({ children }) {
  const [dialogState, setDialogState] = useState({
    open: false,
    title: "",
    message: "",
    type: "confirm",
    confirmText: undefined,
    cancelText: "Hủy",
    loading: false,
  });

  const resolverRef = useRef(null);

  const confirm = useCallback((options) => {
    if (!options?.title) {
      console.warn("[useConfirm] 'title' là tham số bắt buộc!");
    }

    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setDialogState({
        open: true,
        title: options.title || "Xác nhận",
        message: options.message || "",
        type: options.type || "confirm",
        confirmText: options.confirmText,
        cancelText: options.cancelText || "Hủy",
        confirmColor: options.confirmColor,
        size: options.size || "xs",
        loading: false,
      });
    });
  }, []);

  const handleClose = () => {
    setDialogState((prev) => ({ ...prev, open: false, loading: false }));
    if (resolverRef.current) {
      resolverRef.current(false);
      resolverRef.current = null;
    }
  };

  const handleConfirm = () => {
    setDialogState((prev) => ({ ...prev, open: false, loading: false }));
    if (resolverRef.current) {
      resolverRef.current(true);
      resolverRef.current = null;
    }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AppConfirmDialog
        open={dialogState.open}
        title={dialogState.title}
        message={dialogState.message}
        type={dialogState.type}
        confirmText={dialogState.confirmText}
        cancelText={dialogState.cancelText}
        confirmColor={dialogState.confirmColor}
        size={dialogState.size}
        loading={dialogState.loading}
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
    </ConfirmContext.Provider>
  );
}

/**
 * Hook useConfirm - Trả về hàm gọi Dialog xác nhận
 *
 * @example
 * const confirm = useConfirm();
 * const handleDelete = async (row) => {
 *   const ok = await confirm({
 *     title: "Xác nhận xóa kỳ nghiệm thu", // Bắt buộc
 *     message: "Toàn bộ dữ liệu chi tiết sẽ bị xóa. Bạn có chắc chắn?",
 *     type: "delete",
 *   });
 *   if (ok) {
 *     await api.delete(row.id);
 *   }
 * };
 */
export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error("useConfirm() phải được sử dụng bên trong <ConfirmProvider />");
  }
  return context;
}
